import React, {useCallback, useEffect, useRef, useState} from 'react';
import {WithTranslation} from 'react-i18next';
import {AppState, Platform, ScrollView, View} from 'react-native';
import * as Keychain from 'react-native-keychain';
import {noop} from 'lodash';

import KeyboardFocusable from '/component/base/ExtKeyboard/KeyboardFocusable';
import Icon from '/component/base/Icon';
import Text from '/component/base/Text';
import Gutter from '/component/layout/Gutter';
import NavigationHeader from '/component/layout/NavigationHeader';
import {
  handleBiometricsOptIn,
  handlePinOptIn,
  useGetItems,
  useGetPinItems,
} from '/component/partial/setting/SecuritySettings/SecuritySettings.util';
import SwitchSection from '/component/partial/setting/SwitchSection';
import {Settings} from '/constant/TestIDs';
import {
  BIOMETRIC_REQUESTED,
  HAS_OPTED_INTO_BIOMETRICS,
  HAS_SETUP_PIN,
  USER_PIN,
} from '/enum/storage.enum';
import {withPopup} from '/provider/PopupProvider';
import {withTranslation} from '/provider/TranslationProvider';
import {useStorage} from '/util/async-storage/useStorage';
import {getBiometricsUtilDefaultInstance} from '/util/biometrics/biometrics.util';
import {withHOC} from '/util/hoc.util';
import {getOneTrustUtilDefaultInstance} from '/util/oneTrust';
import testProperties from '/util/testing/testProperties';

import {styles} from './SecuritySettings.style';

interface SecuritySettingsProps extends WithTranslation {
  tt: (key: string, defaultValue: string) => string;
  showPopup: () => void;
  hidePopup: () => void;
}

export const SecuritySettings: React.FC<SecuritySettingsProps> = ({
  tt = withTranslation.ttDefaultProp,
  showPopup = noop,
  hidePopup = noop,
}: SecuritySettingsProps) => {
  const [biometricsOptIn, setBiometricsOptIn] = useState<boolean | undefined>();
  const [biometricType, setBiometricType] = useState<
    Keychain.BIOMETRY_TYPE | undefined
  >();
  const [pinOptIn, setPinOptIn] = useState<boolean | undefined>();
  const {setValue: setBiometricsOptInValue, getValue: getBiometricsOptInValue} =
    useStorage({
      key: HAS_OPTED_INTO_BIOMETRICS,
      defaultValue: false,
    });

  const {
    setValue: setbiometricsWasRequested,
    getValue: getbiometricsWasRequested,
  } = useStorage({
    key: BIOMETRIC_REQUESTED,
    defaultValue: false,
  });

  const {setValue: setPinOptInValue, getValue: getPinOptInValue} = useStorage({
    key: HAS_SETUP_PIN,
    defaultValue: false,
  });

  const {getValue: getUserPinValue} = useStorage({
    key: USER_PIN,
    defaultValue: false,
  });

  const appState = useRef(AppState.currentState);

  const checkFaceIDStatus = useCallback(async () => {
    const isBiometricRequested = await getbiometricsWasRequested();
    if (!isBiometricRequested) {
      setBiometricsOptInValue(false);
    } else {
      try {
        const biometryType = await Keychain.getSupportedBiometryType();
        if (Platform.OS === 'ios') {
          setBiometricsOptInValue(
            biometryType === Keychain.BIOMETRY_TYPE.FACE_ID,
          );
        } else if (Platform.OS === 'android') {
          setBiometricsOptInValue(
            biometryType === Keychain.BIOMETRY_TYPE.FINGERPRINT,
          );
        } else {
          setBiometricsOptInValue(false);
        }
        setBiometricsOptIn(await getBiometricsOptInValue());
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error checking biometric status:', error);
      }
    }
  }, [setBiometricsOptInValue, getbiometricsWasRequested]);

  useEffect(() => {
    const subscription = AppState.addEventListener('change', nextAppState => {
      if (
        appState.current.match(/inactive|background/) &&
        nextAppState === 'active'
      ) {
        checkFaceIDStatus();
      }
      appState.current = nextAppState;
    });
    return () => {
      subscription.remove();
    };
  }, [biometricType, getBiometricsOptInValue, getPinOptInValue]);

  useEffect(() => {
    checkFaceIDStatus();
  }, []);

  useEffect(() => {
    getBiometricsUtilDefaultInstance().getBiometricType({
      setBiometricType,
      biometricType,
    });
    getBiometricsOptInValue().then(val => {
      setBiometricsOptIn(val);
    });
    getPinOptInValue().then(val => setPinOptIn(val));
  }, [
    biometricType,
    getBiometricsOptInValue,
    getPinOptInValue,
    setBiometricsOptInValue,
  ]);

  const items = useGetItems({
    biometricType,
    handleBiometricsOptIn: handleBiometricsOptIn({
      showPopup,
      setBiometricsOptInValue,
      setBiometricsOptIn,
      hidePopup,
      getBiometricsOptInValue,
      biometricsOptIn,
      pinOptIn,
      setPinOptIn,
      getUserPinValue,
      biometricType,
      setbiometricsWasRequested,
    }),
    biometricsOptIn,
  });

  const pinItems = useGetPinItems({
    biometricType,
    biometricsOptIn,
    handlePinOptIn: handlePinOptIn({
      showPopup,
      setPinOptInValue,
      setPinOptIn,
      hidePopup,
      getUserPinValue,
      pinOptIn,
      getPinOptInValue,
    }),
    pinOptIn,
    showPopup,
    hidePopup,
  });

  return (
    <NavigationHeader
      testID={Settings.securityScreen}
      style={{base: styles.base}}>
      <View style={styles.mainBase}>
        <View style={styles.header}>
          <Gutter
            accessible={true}
            accessibilityLabel={`${tt('title', 'Security & tracking')}. ${tt(
              'description',
              'Here you can manage your app login settings & tracking preferences',
            )}`}
            accessibilityRole="header"
            importantForAccessibility="yes">
            <Text
              testID={Settings.securityTitle}
              style={styles.heading}
              variant="large">
              {tt('title', 'Security & tracking')}
            </Text>
            <Text
              testID={Settings.securityDescription}
              variant="small"
              emphasis="low">
              {tt(
                'description',
                'Here you can manage your app login settings & tracking preferences',
              )}
            </Text>
          </Gutter>
        </View>

        <View style={styles.trackingContainer} accessible={false}>
          <KeyboardFocusable
            testID="trackingPreferencesTouchable"
            style={styles.trackingPref}
            accessible={true}
            accessibilityRole="button"
            accessibilityLabel={tt(
              'TrackingPreferences',
              'Tracking preferences',
            )}
            onPress={() => getOneTrustUtilDefaultInstance().showBanner()}
            {...testProperties(null, true)}>
            <View style={styles.trackingIconText}>
              <Icon name="location" style={styles.locIcon} />
              <Text variant="small" testID={Settings.trackingPreferencesLabel}>
                {tt('TrackingPreferences', 'Tracking preferences')}
              </Text>
            </View>
            <Icon name="chevron-right" />
          </KeyboardFocusable>
        </View>

        <ScrollView>
          <SwitchSection items={items} />
          <SwitchSection items={pinItems} />
        </ScrollView>
      </View>
    </NavigationHeader>
  );
};

export default withHOC(
  withTranslation('screen.SecuritySettings'),
  withPopup(),
)(React.memo(SecuritySettings));
